import React, { useMemo } from 'react';
import cx from 'clsx';
import Skeleton from 'react-loading-skeleton';

import {
  OfficialAccountAvatar,
  OfficialAccountFollowBtn,
  OrgVerifiedLogo,
  UserCardPopup,
} from 'components';
import { useTheme, OfficialAccountVerifyStatus, useSwitch } from 'hooks';
import { IconLoadingAvatar, IconLoadingAvatarDark } from '@/assets/icon/avatar';
import HiOperateMenu, { HiOperateMenuType } from '../HiOperateMenu';
import style from './index.module.css';
import { convertOfficialIdToHiId, getOfficialPath, openNewWindow } from '@/utils';
import { HiSession } from '../../types';

type HeaderOfficialAccountInfoProps = {
  officialAccountInfo: OfficialAccount;
  hiSession?: HiSession;
  hideOperationMenu?: boolean;
};

const OfficialAccountCardPopoverWrapper = ({
  children,
  officialAccount,
  className,
}: {
  children: React.ReactNode;
  officialAccount: OfficialAccount;
  className?: string;
}) => {
  return (
    <UserCardPopup
      className={className}
      address={officialAccount.id}
      hideSayHiButton
      position={['bottom left', 'bottom center']}>
      {children}
    </UserCardPopup>
  );
};

export const HeaderOfficialAccountInfo = ({
  officialAccountInfo,
  hiSession,
  hideOperationMenu,
}: HeaderOfficialAccountInfoProps) => {
  const sessionId = hiSession?.id;
  const { isDark } = useTheme();
  const LoadingAvatar = useMemo(
    () => (isDark ? IconLoadingAvatarDark : IconLoadingAvatar),
    [isDark],
  );
  const { on: showAll, turnOn: setShowAll } = useSwitch(false);
  const unverified = officialAccountInfo?.verify_status !== OfficialAccountVerifyStatus.VERIFIED;
  const displayName = useMemo(() => officialAccountInfo?.name, [officialAccountInfo?.name]);

  return (
    <div className={style.headerInfo}>
      <div className={style.userInfo}>
        <div className={style.userInfoMain}>
          {!officialAccountInfo ? (
            <LoadingAvatar className={style.avatar} height={50} width={50}></LoadingAvatar>
          ) : (
            <OfficialAccountCardPopoverWrapper officialAccount={officialAccountInfo}>
              <span onClick={() => openNewWindow(getOfficialPath(officialAccountInfo), '_blank')}>
                <OfficialAccountAvatar
                  className={style.avatar}
                  logoVisible={false}
                  size={50}
                  data={officialAccountInfo}
                  userCardPopup={{
                    hideSayHiButton: true,
                  }}
                  logoOption={{ size: 18, customStyle: { bottom: 0, right: 0 } }}
                />
              </span>
            </OfficialAccountCardPopoverWrapper>
          )}
          <div className={style.userInfoContent}>
            <div className={style.uid}>
              {officialAccountInfo ? (
                <>
                  <span
                    onClick={() => openNewWindow(getOfficialPath(officialAccountInfo), '_blank')}
                    className={cx(style.accountName)}>
                    <OfficialAccountCardPopoverWrapper
                      officialAccount={officialAccountInfo}
                      className={style.userInfoText}>
                      {displayName}
                    </OfficialAccountCardPopoverWrapper>
                  </span>

                  <OrgVerifiedLogo
                    marginLeft={0}
                    marginRight={20}
                    size={20}
                    unverified={unverified}
                  />

                  <OfficialAccountFollowBtn
                    className={style.followBtn}
                    officialId={officialAccountInfo.id}
                  />
                </>
              ) : (
                <Skeleton width={264} height={24}></Skeleton>
              )}
            </div>

            <span className={cx(style.intro, showAll && style.showAll)} onClick={setShowAll}>
              {officialAccountInfo ? (
                officialAccountInfo.intro
              ) : (
                <Skeleton width={264} height={20}></Skeleton>
              )}
            </span>
          </div>
        </div>
      </div>

      {hideOperationMenu ? null : (
        <div className={style.operateMenuWrapper}>
          <HiOperateMenu
            config={{
              otherSideHiId: convertOfficialIdToHiId(officialAccountInfo?.id),
              otherSideName: displayName,
              sessionId: sessionId!,
            }}
            menuTypeList={
              hiSession
                ? [
                    hiSession?.is_pinned ? HiOperateMenuType.UNPIN : HiOperateMenuType.PIN,
                    HiOperateMenuType.ARCHIVE,
                    HiOperateMenuType.REPORT,
                  ]
                : []
            }
          />
        </div>
      )}
    </div>
  );
};

export default HeaderOfficialAccountInfo;
